import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Inbox, Loader2, Download } from "lucide-react";
import { downloadEnquiriesCsv } from "@/lib/enquiriesCsv";
import { Logo } from "@/components/site/Logo";
import { EnquiryCard, STATUSES } from "@/components/admin/EnquiryCard";
import { adminApi } from "@/lib/adminApi";
import { errMsg, notifyError } from "@/lib/appsApi";
import { useNoIndex } from "@/lib/useNoIndex";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  useNoIndex();

  const loadCounts = useCallback(async (query) => {
    const res = await adminApi.get("/admin/enquiries/counts", { params: { q: query } }).catch(() => null);
    setCounts(res?.data ?? {});
  }, []);

  const load = useCallback(async (query, st) => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/enquiries", { params: { q: query, status: st } });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    adminApi.get("/auth/me").then((r) => setUser(r?.data ?? null)).catch(() => adminApi.post("/auth/refresh").then((r) => setUser(r?.data ?? null)).catch(() => nav("/admin/login")));
  }, [nav]);

  useEffect(() => {
    if (!user) return undefined;
    const t = setTimeout(() => load(q, status), 300);
    return () => clearTimeout(t);
  }, [q, status, user, load]);

  useEffect(() => {
    if (!user) return undefined;
    const t = setTimeout(() => loadCounts(q), 300);
    return () => clearTimeout(t);
  }, [q, user, loadCounts]);

  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => null);
    nav("/admin/login");
  };

  const changeStatus = async (id, st) => {
    try {
      const res = await adminApi.patch(`/admin/enquiries/${id}`, { status: st });
      setItems((list) => (status && status !== st ? list.filter((e) => e.id !== id) : list.map((e) => (e.id === id ? { ...e, ...(res?.data ?? {}) } : e))));
      toast.success(`Marked as ${STATUSES.find((s) => s.id === st)?.label ?? st}`);
      loadCounts(q);
    } catch (err) {
      notifyError(errMsg(err, "Updating the enquiry status"));
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/enquiries/${id}`);
      setItems((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
      loadCounts(q);
    } catch (err) {
      notifyError(errMsg(err, "Deleting the enquiry"));
    }
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F9FD]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 border-b border-sky-100 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <a href="/" data-testid="admin-home-link"><Logo /></a>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink transition-colors hover:border-sky-600 hover:text-sky-700" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" />Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Signed in as {user.email}</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">Enquiries</h1>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-sky-600 px-3 py-1 font-mono text-xs text-white" data-testid="admin-enquiry-count">{items.length} {q || status ? "found" : "total"}</span>
            <button
              onClick={() => { downloadEnquiriesCsv(items, status || (q ? "search" : "all")); toast.success(`Downloaded ${items.length} enquir${items.length === 1 ? "y" : "ies"}`); }}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink transition-colors hover:border-sky-600 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="admin-download-csv-button"
            >
              <Download className="h-4 w-4" />Download spreadsheet
            </button>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-status-filter">
          {[{ id: "", label: "All" }, ...STATUSES].map((s) => (
            <button
              key={s.id || "all"}
              onClick={() => setStatus(s.id)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${status === s.id ? "border-ink bg-ink text-white" : "border-slate-200 bg-white text-slate-700 hover:border-sky-600 hover:text-sky-700"}`}
              data-testid={`admin-filter-${s.id || "all"}`}
            >
              {s.label}
              <span className={`ml-2 rounded-full px-2 py-0.5 font-mono text-xs ${status === s.id ? "bg-white/20 text-white" : "bg-sky-50 text-sky-800"}`} data-testid={`admin-filter-count-${s.id || "all"}`}>
                {counts?.[s.id || "all"] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <label className="relative mt-4 block">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, organisation or message" className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-base text-ink outline-none transition-colors focus:border-sky-600" data-testid="admin-search-input" />
        </label>
        <div className="mt-8 space-y-4" data-testid="admin-enquiry-list">
          {loading && items.length === 0 && <Loader2 className="mx-auto h-6 w-6 animate-spin text-sky-600" />}
          {!loading && items.length === 0 && (
            <div className="rounded-3xl border border-dashed border-sky-200 bg-white p-12 text-center" data-testid="admin-empty-state">
              <Inbox className="mx-auto h-8 w-8 text-sky-500" />
              <p className="mt-3 text-slate-600">{q || status ? "No enquiries match this view." : "No enquiries yet."}</p>
            </div>
          )}
          {items.map((e) => <EnquiryCard key={e.id} e={e} onDelete={remove} onStatus={changeStatus} />)}
        </div>
      </main>
    </div>
  );
}
